
import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Moon, Sun, Monitor, Palette, Contrast, Eye } from 'lucide-react';
import { toast } from 'sonner';

export const AppearanceSettings = () => {
  const [theme, setTheme] = useState('light');
  const [contrastMode, setContrastMode] = useState('normal');

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const savedContrast = localStorage.getItem('contrast-mode');
    
    // التحقق من إعدادات النظام
    if (savedTheme === 'system' || !savedTheme) {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      applyTheme(savedTheme || 'system', systemTheme);
      setTheme(savedTheme || 'system');
    } else {
      applyTheme(savedTheme, savedTheme);
      setTheme(savedTheme);
    }
    
    if (savedContrast) {
      setContrastMode(savedContrast);
      applyContrastMode(savedContrast);
    }

    // الاستماع لتغييرات إعدادات النظام
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      if (theme === 'system') {
        applyTheme('system', e.matches ? 'dark' : 'light');
      }
    };
    mediaQuery.addEventListener('change', handleChange);
    
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const applyTheme = (selectedTheme: string, actualTheme: string) => {
    if (selectedTheme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      document.documentElement.className = systemTheme === 'dark' ? 'dark' : '';
    } else {
      document.documentElement.className = actualTheme === 'dark' ? 'dark' : '';
    }
  };

  const applyContrastMode = (mode: string) => {
    document.documentElement.setAttribute('data-contrast', mode);
    
    // تطبيق أنماط التباين المحسنة
    if (mode === 'high') {
      document.documentElement.style.setProperty('--border', '0 0% 20%');
      document.documentElement.style.setProperty('--muted-foreground', '0 0% 30%');
    } else {
      // إعادة القيم الافتراضية
      document.documentElement.style.removeProperty('--border');
      document.documentElement.style.removeProperty('--muted-foreground');
    }
  };

  const changeTheme = (newTheme: string) => {
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    
    if (newTheme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      applyTheme(newTheme, systemTheme);
      toast.success(`تم التحويل إلى الوضع التلقائي (${systemTheme === 'dark' ? 'داكن' : 'فاتح'})`);
    } else {
      applyTheme(newTheme, newTheme);
      toast.success(`تم التحويل إلى الوضع ${newTheme === 'dark' ? 'الداكن' : 'الفاتح'}`);
    }
  };

  const changeContrastMode = (newMode: string) => {
    setContrastMode(newMode);
    localStorage.setItem('contrast-mode', newMode);
    applyContrastMode(newMode);
    toast.success(`تم تطبيق وضع التباين ${newMode === 'high' ? 'العالي' : 'العادي'}`);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5" />
            إعدادات المظهر
          </CardTitle>
          <CardDescription>تخصيص ألوان ومظهر النظام</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label>السمة</Label>
            <Select value={theme} onValueChange={changeTheme}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="light" className="flex items-center gap-2">
                  <Sun className="h-4 w-4 inline ml-2" />
                  فاتح
                </SelectItem>
                <SelectItem value="dark" className="flex items-center gap-2">
                  <Moon className="h-4 w-4 inline ml-2" />
                  داكن
                </SelectItem>
                <SelectItem value="system" className="flex items-center gap-2">
                  <Monitor className="h-4 w-4 inline ml-2" />
                  تلقائي (حسب النظام)
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              {theme === 'system' 
                ? 'يتم التبديل تلقائياً حسب إعدادات نظام التشغيل'
                : theme === 'dark'
                ? 'الوضع الداكن مفعل - مناسب للإضاءة المنخفضة'
                : 'الوضع الفاتح مفعل - مناسب للإضاءة العادية'
              }
            </p>
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Contrast className="h-4 w-4" />
              وضع التباين
            </Label>
            <Select value={contrastMode} onValueChange={changeContrastMode}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="normal">عادي</SelectItem>
                <SelectItem value="high">تباين عالي</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              وضع التباين العالي يحسن القراءة لضعاف البصر
            </p>
          </div>
        </CardContent>
      </Card>

      {/* معاينة المظهر */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            معاينة المظهر
          </CardTitle>
          <CardDescription>شاهد كيف ستظهر الألوان في الوضع الحالي</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <div className="p-4 rounded-lg bg-primary text-primary-foreground text-center">
                الأساسي
              </div>
              <p className="text-xs text-center text-muted-foreground">Primary</p>
            </div>
            <div className="space-y-2">
              <div className="p-4 rounded-lg bg-secondary text-secondary-foreground text-center">
                الثانوي
              </div>
              <p className="text-xs text-center text-muted-foreground">Secondary</p>
            </div>
            <div className="space-y-2">
              <div className="p-4 rounded-lg bg-accent text-accent-foreground text-center">
                التمييز
              </div>
              <p className="text-xs text-center text-muted-foreground">Accent</p>
            </div>
            <div className="space-y-2">
              <div className="p-4 rounded-lg bg-success text-success-foreground text-center">
                نجاح
              </div>
              <p className="text-xs text-center text-muted-foreground">Success</p>
            </div>
            <div className="space-y-2">
              <div className="p-4 rounded-lg bg-warning text-warning-foreground text-center">
                تحذير
              </div>
              <p className="text-xs text-center text-muted-foreground">Warning</p>
            </div>
            <div className="space-y-2">
              <div className="p-4 rounded-lg bg-destructive text-destructive-foreground text-center">
                خطر
              </div>
              <p className="text-xs text-center text-muted-foreground">Danger</p>
            </div>
          </div>

          <div className="mt-6 space-y-3">
            <div className="p-4 rounded-lg border bg-card">
              <h4 className="font-semibold mb-2">كارت عادي</h4>
              <p className="text-sm text-muted-foreground">
                هذا مثال على كيفية ظهور الكروت في الوضع الحالي
              </p>
            </div>
            <div className="p-4 rounded-lg bg-muted">
              <h4 className="font-semibold mb-2">خلفية مخففة</h4>
              <p className="text-sm text-muted-foreground">
                تستخدم للأقسام الثانوية والمعلومات الإضافية
              </p>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            <Button variant="default">زر أساسي</Button>
            <Button variant="secondary">زر ثانوي</Button>
            <Button variant="outline">زر محدد</Button>
            <Button variant="ghost">زر شفاف</Button>
            <Button variant="destructive">زر حذف</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
